/**
 * SectionCard styles
 *
 * Layout matches the design:
 *  - Outer `.card` is just a column container — no border itself.
 *  - `.expandedBody` stacks the content card, triggers, questions and footer.
 *  - The inner `.contentCard` carries the section-level left-border state
 *    (blue/green/red), mirroring V1's per-question `bottomSection*` pattern.
 *
 * The old `.triggerSubtitle` paragraph + `formatTriggerSubtitle` helper were
 * removed once `SectionTriggers` moved outside the expansion gate — the trigger
 * rows now display the rule directly in styled form, so the subtitle was
 * redundant (and was rendering raw option UUIDs due to a stale fieldId/id mixup).
 */
import { StyleSheet } from 'aphrodite';
import { THEME } from '../../../../../../components-v2/Theme/theme';

export const styleSheet = StyleSheet.create({
	/** Outer card wrapper — column layout, no border (the bar + inner card do the visual work). */
	card: {
		display: 'flex',
		flexDirection: 'column',
		marginBottom: THEME.spacing.md,
		gap: THEME.spacing.xs,
	},

	/** Inline validation hint rendered below SectionHeader when the section is invalid. */
	validationHint: {
		color: THEME.colors.danger.main,
		fontSize: THEME.typography.fontSizes.xs,
		margin: 0,
		paddingLeft: THEME.spacing.md,
	},

	/** Wrapper for the expanded body (content card + triggers + questions + footer). */
	expandedBody: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.md,
		paddingTop: THEME.spacing.xs,
	},

	/** Inner content card hosting the section title/description editor. Carries the state border. */
	contentCard: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.sm,
		padding: THEME.spacing.md,
		backgroundColor: THEME.colors.background.light,
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadius,
	},

	/** Blue border — new section or section with unsaved changes. */
	contentNew: {
		borderLeft: `4px solid ${THEME.colors.primary.main}`,
	},

	/** Green border — persisted section with no pending changes. */
	contentSaved: {
		borderLeft: `4px solid ${THEME.colors.success.main}`,
	},

	/** Red border — section-level validation error. */
	contentError: {
		borderLeft: `4px solid ${THEME.colors.danger.main}`,
	},

	/** Label above each editable section-meta field. */
	fieldLabel: {
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: 600,
		color: THEME.colors.text.dark,
	},

	/** Common style for the title/description inputs inside the content card. */
	field: {
		width: '100%',
	},
});
